import { useRef } from 'react';
import { useAtomValue } from 'jotai';
import { cn } from '@rocco/utils/cn';
import { ReactComponent as EyeIcon } from '@rocco/icons/svg/eye.svg';
import { ReactComponent as LikeIcon } from '@rocco/icons/svg/like.svg';
import { shortenCount } from '@rocco/utils/format-number';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProjectTileViewModel } from '@rocco/ui/tile-project/view-models/project';
import { TileMedia } from '@rocco/ui/tile-shared/components/TileMedia';
import { TileBadge } from '@rocco/ui/tile-shared/components/TileBadge';
import { TileFollowButton } from '@rocco/ui/tile-shared/components/TileFollowButton';
import { ProjectTileCounterBadge } from './ProjectTileCounterBadge';
import { uiContextAtomTileActions } from '@rocco/ui/tile-shared/hooks/use-shared-tiles';
import { useTracker } from '../../../../../tracker';
import { trackTileMediaDetailClick } from '../../../../../web/app/modules/tracking/util/trackTileMedia';
import { OptionalLink } from '@rocco/components/link/OptionalLink';
import { TILE_COMPONENTS_CLASS_NAMES } from '@rocco/ui/tile-shared/utils/contstants';

const PROJECT_TILE_MIN_VIEW_COUNT_THRESHOLD = 1;
const PROJECT_TILE_MIN_SAVED_COUNT_THRESHOLD = 100;

export const ProjectTile = (props: TileViewModel<ProjectTileViewModel>) => {
    const {
        aspectRatio,
        model: project,
        section,
        enableTileSlider,
        disableTileSliderABTest,
        isFollowed,
        onFollow,
        routeState,
        hideFollowButton = false,
        followButton,
        overlay,
        inlineActionButtons,
        disableLink = false,
        onClick,
        className,
        contentEditable,
        eager,
    } = props;
    const tileActions = useAtomValue(uiContextAtomTileActions);
    const tileRef = useRef<HTMLAnchorElement>(null);
    const tracker = useTracker();

    const handleTileClick = () => {
        onClick?.();
        tileActions?.trackingConfig?.onClick?.(
            tileRef,
            project.id,
            project.professional.id,
            section,
        );
        trackTileMediaDetailClick(tracker, {
            itemID: project.id,
            itemType: 'ProjectTile',
            itemName: project.title,
        });
    };

    return (
        <OptionalLink
            ref={tileRef}
            mode={disableLink ? 'override' : 'normal'}
            to={project.link}
            prefetch="intent"
            state={routeState}
            onClick={handleTileClick}
        >
            <div className={cn('flex flex-col gap-4', className)}>
                <div
                    className={cn(
                        'group relative',
                        aspectRatio === 'featured' ? 'h-full' : undefined,
                    )}
                >
                    <TileMedia
                        aspectRatio={aspectRatio}
                        images={project.images}
                        video={project.video}
                        enableTileSlider={enableTileSlider}
                        disableTileSliderABTest={disableTileSliderABTest}
                        eager={eager}
                        onSlideChange={(direction) => {
                            tileActions?.trackingConfig?.onTileMediaSliderSlideChange?.(
                                direction,
                                project.id,
                            );
                        }}
                    />

                    {overlay ? (
                        overlay
                    ) : (
                        <>
                            <div className="absolute inset-0 hidden rounded-tile bg-gray-900/10 opacity-0 transition-opacity duration-300 md:block md:group-hover:opacity-100" />
                            <div className="absolute right-4 top-3.5 flex gap-1 transition-opacity duration-300 md:opacity-0 md:group-hover:opacity-100">
                                {followButton
                                    ? followButton
                                    : !hideFollowButton && (
                                          <TileFollowButton
                                              pinId={project.id}
                                              pinType="ProjectPin"
                                              isFollowed={isFollowed}
                                              onFollow={onFollow}
                                          />
                                      )}
                            </div>
                        </>
                    )}

                    {project.badge && (
                        <div className="absolute left-4 top-3 transition-opacity duration-300 md:group-hover:opacity-0">
                            <TileBadge title={project.badge} />
                        </div>
                    )}
                </div>

                <div className="flex flex-col gap-4">
                    {inlineActionButtons && (
                        <div className="flex flex-col">
                            {inlineActionButtons}
                        </div>
                    )}
                    <div
                        className={cn(
                            TILE_COMPONENTS_CLASS_NAMES.TILE_DESCRIPTION_BLOCK,
                            'flex flex-col gap-[0.125rem]',
                        )}
                    >
                        <span
                            contentEditable={contentEditable?.enabled.title}
                            role={
                                contentEditable?.enabled.title
                                    ? 'textbox'
                                    : undefined
                            }
                            aria-label={
                                contentEditable?.enabled.title
                                    ? 'Edit title'
                                    : undefined
                            }
                            aria-multiline="false"
                            suppressContentEditableWarning={true}
                            onInput={(e: React.FormEvent<HTMLSpanElement>) =>
                                contentEditable?.onChange?.(
                                    (e.target as HTMLSpanElement).textContent ??
                                        '',
                                    'title',
                                )
                            }
                            onBlur={() => {
                                contentEditable?.onBlur?.();
                            }}
                            onKeyDown={(
                                e: React.KeyboardEvent<HTMLSpanElement>,
                            ) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    (e.target as HTMLSpanElement).blur();
                                }
                            }}
                            className={cn(
                                'overflow-hidden text-ellipsis text-sm font-medium tracking-[0.03rem] md:text-base md:tracking-[0.02rem]',
                                !contentEditable?.enabled.title &&
                                    'text-nowrap',
                                contentEditable?.enabled.title &&
                                    'focus-within:border-primary-500 -mx-2 cursor-text rounded-md border border-transparent px-2 focus-within:bg-gray-100 hover:border-gray-500',
                            )}
                        >
                            {project.title}
                        </span>
                        <span
                            contentEditable={
                                contentEditable?.enabled.description
                            }
                            role={
                                contentEditable?.enabled.description
                                    ? 'textbox'
                                    : undefined
                            }
                            aria-label={
                                contentEditable?.enabled.description
                                    ? 'Edit description'
                                    : undefined
                            }
                            aria-multiline="false"
                            suppressContentEditableWarning={true}
                            onInput={(e: React.FormEvent<HTMLSpanElement>) =>
                                contentEditable?.onChange?.(
                                    (e.target as HTMLSpanElement).textContent ??
                                        '',
                                    'description',
                                )
                            }
                            onBlur={() => {
                                contentEditable?.onBlur?.();
                            }}
                            onKeyDown={(
                                e: React.KeyboardEvent<HTMLSpanElement>,
                            ) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    (e.target as HTMLSpanElement).blur();
                                }
                            }}
                            className={cn(
                                'overflow-hidden text-ellipsis text-sm font-medium tracking-[0.03rem] text-gray-900/40 md:text-base md:tracking-[0.02rem]',
                                !contentEditable?.enabled.description &&
                                    'text-nowrap',
                                contentEditable?.enabled.description &&
                                    'focus-within:border-primary-500 -ml-2 cursor-text rounded-md border border-transparent px-2 focus-within:bg-gray-100 hover:border-gray-500',
                            )}
                        >
                            {project.professional.name}
                        </span>
                    </div>

                    <div className="flex justify-start gap-[0.5625rem] empty:hidden md:pb-[0.325rem]">
                        {!!project.viewedCount &&
                            project.viewedCount >
                                PROJECT_TILE_MIN_VIEW_COUNT_THRESHOLD && (
                                <ProjectTileCounterBadge
                                    icon={EyeIcon}
                                    value={shortenCount(
                                        project.viewedCount ?? 0,
                                    )}
                                />
                            )}
                        {!!project.savedCount &&
                            project.savedCount >
                                PROJECT_TILE_MIN_SAVED_COUNT_THRESHOLD && (
                                <ProjectTileCounterBadge
                                    icon={LikeIcon}
                                    value={shortenCount(
                                        project.savedCount ?? 0,
                                    )}
                                />
                            )}
                    </div>
                </div>
            </div>
        </OptionalLink>
    );
};
